import { ErrorModal } from "@dexteel/mesf-core";
import { Box } from "@mui/material";
import { useEffect, useMemo } from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { RecipeEditorPanel } from "../../straightener-common/components/RecipeEditorPanel";
import { useStraightenerRecipeEditor } from "../../straightener-common/hooks/useStraightenerRecipeEditor";
import type {
	ProductNode,
	StraightenerProduct,
	StraightenerRecipeVersion,
} from "../../straightener-recipes/models/StraightenerRecipe";

interface Props {
	// The current PLC product — the only product this editor ever writes to.
	product: StraightenerProduct;
	// All recipe versions of the current product (to resolve the edited one).
	versions: StraightenerRecipeVersion[];
	// Copy-from sources (all products with a current recipe).
	allNodes: ProductNode[];
	defaultCrew: string;
	// The recipe being edited; null => a new recipe. Controlled by the parent
	// (the Select / Edit / Create actions live in the banner's actions bar).
	recipeId: number | null;
	// The recipe the banner picker shows: Copy From opens on it.
	copySourceRecipeId: number | null;
	onRecipeIdChange: (recipeId: number | null) => void;
	onClose: () => void;
	onChanged: () => void;
}

// The collapsible editor scoped to the current product. Selection (which
// version, or new) is driven from the banner actions; this component just edits
// whatever `recipeId` it is handed, reusing the shared editor + actions.
export const InlineRecipeEditor = ({
	product,
	versions,
	allNodes,
	defaultCrew,
	recipeId,
	copySourceRecipeId,
	onRecipeIdChange,
	onClose,
	onChanged,
}: Props) => {
	const editor = useStraightenerRecipeEditor({
		onSavedNew: (newId) => {
			onRecipeIdChange(newId);
			onChanged();
		},
		onUpdated: onChanged,
	});

	// Seed a new recipe with the current line values when entering new mode.
	// biome-ignore lint/correctness/useExhaustiveDependencies: seed only on entering new mode
	useEffect(() => {
		if (recipeId == null) void editor.prefillFromCurrentLine();
	}, [recipeId]);

	const selectedVersion = versions.find((v) => v.Id === recipeId) ?? null;

	// Names of the product's other recipes (lower-cased) for the unique-per-
	// product check — same rule as the Recipes screen, excluding the one edited.
	const existingNames = useMemo<string[]>(
		() =>
			versions
				.filter((v) => v.Id !== recipeId)
				.map((v) => (v.Name ?? "").trim().toLowerCase())
				.filter(Boolean),
		[versions, recipeId],
	);

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<RecipeEditorPanel
					product={product}
					recipeId={recipeId}
					version={selectedVersion?.Version ?? null}
					recipeName={selectedVersion?.Name ?? ""}
					selectedVersion={selectedVersion}
					allNodes={allNodes}
					copySourceRecipeId={copySourceRecipeId}
					existingNames={existingNames}
					defaultCrew={defaultCrew}
					editor={editor}
					onClose={onClose}
				/>
			</Box>

			<NotificationSnackBar
				message={editor.notification}
				onHide={() => editor.setNotification("")}
			/>
			<ErrorModal error={editor.error} onHide={() => editor.setError("")} />
		</Box>
	);
};
